import React from 'react';
import {
  ColorValues,
  Flex,
  IconBrandIconToday,
  Spinner,
  Switch,
  Typography,
} from '@banhala/ui-kit';
import { useAtom } from 'jotai';
import { marketGoodsListFilterAtom } from '../../../../states/GoodsListFilter.atom';
import { isAndroid, localizeNumber } from 'shared/src/utils';
import { useLogClickHeadlineXsmallOneLineToggle } from './hooks/useLogClickHeadlineXsmallOneLineToggle';
import { DeliveryType } from 'shared/src/enums';
import { useMarketHomeGoods } from '../../../../hooks/useMarketHomeGoods';
import { ErrorBoundary } from 'shared/src/component';

const GoodsCountHeaderContent = ({ marketId }: { marketId: number }) => {
  const [filter, setFilter] = useAtom(marketGoodsListFilterAtom);
  const { totalCount, hasTodayGoods, hasShakGoods, listId, isLoading } =
    useMarketHomeGoods(marketId, filter[marketId]);

  const logClickHeadlineXsmallOneLineToggle =
    useLogClickHeadlineXsmallOneLineToggle({ listId, toggleType: 'today' });

  const isFastDeliveryEnabled =
    filter[marketId].deliveryType === DeliveryType.Fast;

  const handleToggle = (value: boolean) => {
    logClickHeadlineXsmallOneLineToggle(value);
    setFilter(prev => ({
      ...prev,
      [marketId]: {
        ...prev[marketId],
        deliveryType: value ? DeliveryType.Fast : null,
      },
    }));
  };

  const showFastDeliveryToggle = hasTodayGoods || hasShakGoods;

  if (isLoading) return <GoodsCountHeaderSkeleton />;

  if (listId == null) return null;

  return (
    <Flex.Row
      padding={{ horizontal: 12, vertical: 14 }}
      alignItems={'center'}
      justifyContent={'space-between'}
      backgroundColor={'white'}
      height={48}
    >
      <Flex.Row gap={4} alignItems={'center'}>
        <Typography type={'subtitle1'} color={'content_primary'}>
          상품
        </Typography>
        <Typography type={'subtitle1'} color={'content_secondary'}>
          {localizeNumber(totalCount)}
        </Typography>
      </Flex.Row>
      {showFastDeliveryToggle && (
        <Flex.Row alignItems={'center'}>
          <Switch
            scale={isAndroid ? 1.0 : 0.6}
            color={'blue'}
            value={isFastDeliveryEnabled}
            onChange={handleToggle}
            testID={'오늘출발토글'}
          />
          <IconBrandIconToday color={ColorValues.lilac40} />
        </Flex.Row>
      )}
    </Flex.Row>
  );
};

const GoodsCountHeaderSkeleton = () => {
  return (
    <Flex.Row
      padding={{ horizontal: 12 }}
      alignItems={'center'}
      justifyContent={'space-between'}
      backgroundColor={'white'}
      height={48}
    >
      <Flex.Row gap={12} alignItems={'center'}>
        <Typography type={'subtitle1'} color={'content_primary'}>
          상품
        </Typography>
        <Spinner size={8} color={'content_tertiary'} />
      </Flex.Row>

      <Flex
        backgroundColor={'bg_tertiary'}
        width={50}
        height={14}
        borderRadius={4}
      />
    </Flex.Row>
  );
};

export const GoodsCountHeader = ({ marketId }: { marketId: number }) => {
  return (
    // 에러 시 상품 개수 안 보여주기
    <ErrorBoundary ErrorFallback={() => <></>}>
      <GoodsCountHeaderContent marketId={marketId} />
    </ErrorBoundary>
  );
};
